import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import __noop from '@atlaskit/ds-lib/noop';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { LayerNode } from '../classes/layer-node';

/**
 *
 *  @internal
 * LevelContext which maintains the current level of nested layers
 * Default is 0
 */
export var LevelContext = /*#__PURE__*/createContext(0);

/**
 *
 *  @internal
 * LevelNodeContext maintains the current layer node of nested layers.
 * Default ref value is null
 */
export var LevelNodeContext = /*#__PURE__*/createContext({
  current: null
});

/**
 *
 *  @internal
 * RootNodeContext maintains the root node of the layer tree.
 * Default ref value is null
 */
export var RootNodeContext = /*#__PURE__*/createContext({
  current: null
});

/**
 *
 *  @internal @deprecated
 * TopLevelContext which maintains the top level ref and setTopLevel method of layers
 * Default ref value is null
 *
 */
export var TopLevelContext = /*#__PURE__*/createContext({
  topLevelRef: {
    current: null
  },
  layerList: {
    current: null
  },
  setTopLevel: __noop
});

/**
 *
 *  @internal
 * Context Provider Component which provider the current level of nested layers
 * It handles level management when it mounts and unmounts
 *
 */
var LevelProvider = function LevelProvider(_ref) {
  var children = _ref.children,
    currentLevel = _ref.currentLevel,
    levelNode = _ref.node;
  useEffect(function () {
    var levelNodeSafe = levelNode.current;
    var parentNode = levelNodeSafe.parent;
    parentNode === null || parentNode === void 0 || parentNode.addChild(levelNodeSafe);
    return function () {
      parentNode === null || parentNode === void 0 || parentNode.removeChild(levelNodeSafe);
    };
  }, [levelNode]);
  return /*#__PURE__*/React.createElement(LevelContext.Provider, {
    value: currentLevel
  }, /*#__PURE__*/React.createElement(LevelNodeContext.Provider, {
    value: levelNode
  }, children));
};

/**
 *
 *  @internal @deprecated
 * Context Provider Component which provider the current level of nested layers
 * It handles level management when it mounts and unmounts
 *
 */
var LevelProviderOld = function LevelProviderOld(_ref2) {
  var children = _ref2.children,
    currentLevel = _ref2.currentLevel;
  var _useContext = useContext(TopLevelContext),
    layerList = _useContext.layerList;
  // This is not unstable, it will run once for ref creation.
  // eslint-disable-next-line  @repo/internal/react/disallow-unstable-values
  var id = useRef(Math.random().toString(36));
  useEffect(function () {
    var safeLayerList = layerList === null || layerList === void 0 ? void 0 : layerList.current;
    var safeId = id.current;
    if (!safeLayerList) {
      return;
    }
    safeLayerList.push(safeId);
    return function () {
      var index = safeLayerList.indexOf(safeId);
      if (index > -1) {
        safeLayerList.splice(index, 1);
      }
    };
  }, [layerList, id]);
  return /*#__PURE__*/React.createElement(LevelContext.Provider, {
    value: currentLevel
  }, children);
};

/**
 *
 *  @internal @deprecated
 * Context Provider Component which provides the top level of all nested layers
 * It provides initial top level ref value as 0 and set top level method
 *
 */
var LayeringProvider = function LayeringProvider(_ref3) {
  var children = _ref3.children;
  var topLevelRef = useRef(0);
  var layerList = useRef([]);
  var value = useMemo(function () {
    return {
      topLevelRef: topLevelRef,
      setTopLevel: function setTopLevel(level) {
        topLevelRef.current = level;
      },
      layerList: layerList
    };
  }, [topLevelRef, layerList]);
  return /*#__PURE__*/React.createElement(TopLevelContext.Provider, {
    value: value
  }, children);
};

/**
 *
 * @deprecated
 * @important the component is toggled by isDisabled props, the default isDisabled is true
 *
 * Layering component is a wrapper to let children to consume layer contexts and hooks.
 *
 */
var LayeringOld = function LayeringOld(_ref4) {
  var children = _ref4.children,
    _ref4$isDisabled = _ref4.isDisabled,
    isDisabled = _ref4$isDisabled === void 0 ? true : _ref4$isDisabled;
  var currentLevel = useContext(LevelContext);
  if (isDisabled) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  var isNested = currentLevel > 0;
  var content = /*#__PURE__*/React.createElement(LevelProviderOld, {
    currentLevel: currentLevel + 1
  }, children);
  return isNested ? content : /*#__PURE__*/React.createElement(LayeringProvider, null, content);
};

/**
 *
 * @important the component is toggled by isDisabled props, the default isDisabled is true
 *
 * Layering component is a wrapper to let children to consume layer contexts and hooks.
 *
 */
var LayeringNew = function LayeringNew(_ref5) {
  var children = _ref5.children,
    _ref5$isDisabled = _ref5.isDisabled,
    isDisabled = _ref5$isDisabled === void 0 ? true : _ref5$isDisabled;
  var currentLevel = useContext(LevelContext);
  var parentNode = useContext(LevelNodeContext);

  // eslint-disable-next-line  @repo/internal/react/disallow-unstable-values
  var newNode = useRef(new LayerNode(Math.random().toString(36), parentNode.current));
  if (isDisabled) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  var isNested = currentLevel > 0;
  var content = /*#__PURE__*/React.createElement(LevelProvider, {
    currentLevel: currentLevel + 1,
    node: newNode
  }, children);
  return isNested ? content : /*#__PURE__*/React.createElement(RootNodeContext.Provider, {
    value: newNode
  }, content);
};

/**
 *
 * @important the component is toggled by isDisabled props, the default isDisabled is true
 *
 * Layering component is a wrapper to let children to consume layer contexts and hooks.
 * For more information on the implementation: https://hello.atlassian.net/wiki/x/FQCDQwE
 *
 */
export var Layering = componentWithFG('layering-tree-graph', LayeringNew, LayeringOld);